import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

import BottomSheet from './BottomSheet';
import Text from './Text';
import type { Commande } from '../api/useCommandes';
import { formatDate, langueMobile, t } from '../i18n/langue';

/**
 * La feuille de détail d'une commande — #22, #40, #87.
 *
 * **Un objet, une feuille, deux portes** : la même feuille sert le module du
 * dashboard et l'écran « Commandes ». Elle n'est pas dupliquée.
 *
 * ## En lecture seule, par absence d'objet
 *
 * Ce n'est pas une restriction imposée au mobile : sur **453 transitions de
 * statut, 4 ont un auteur humain**, par une seule personne. Le cycle Brouillon →
 * Validé → Terminée se déroule seul, et `use_order_alerts` est actif sur
 * **2 établissements sur 159**. Il n'y a pas d'action à porter.
 *
 * Les deux panneaux de détail du web sont d'ailleurs **déjà en lecture seule** —
 * leur unique action est un lien vers une page d'édition, hors dashboard et non
 * portée. La feuille le dit plutôt que de le taire.
 *
 * ## La conversation est la seule ligne tapotable
 *
 * Correction actée par #22 en cours de ticket : la ligne de liste menait
 * directement à la conversation. C'était une erreur d'appréciation sur le geste
 * réel — **on lit d'abord ce qui a été commandé, on écrit ensuite**.
 *
 * ⚠️ Sur les **88 commandes sans client** (5,2 % — 1 596 sur 1 684 en portent
 * un), cette ligne est **ABSENTE, pas désactivée**. Fil rouge de la carte : ne
 * montrer que ce qui aboutit.
 */
export default function FeuilleCommande({
    commande,
    onFermer,
}: {
    commande: Commande | null;
    onFermer: () => void;
}) {
    const navigate = useNavigate();

    if (!commande) return null;

    const date = formatDate(commande.created_at, {
        day: 'numeric',
        month: 'long',
    });

    return (
        <BottomSheet
            ouverte
            onClose={onFermer}
            libelle={commande.catalog_label ?? t('commandes.feuille.detail')}
            libelleFermeture={t('commandes.feuille.fermer')}
        >
            <div className="flex flex-col gap-4 pb-2">
                <div className="flex flex-col gap-1">
                    <Text variante="sousTitre" as="h2">
                        {commande.catalog_label ?? t('commandes.commande')}
                    </Text>
                    <Text variante="petit" ton="attenue">
                        {t('commandes.feuille.commandeLe', { date })}
                        {commande.guest_room ? ` · ${commande.guest_room}` : ''}
                    </Text>
                    {commande.total_price !== null && (
                        <Text variante="sousTitre" as="p">
                            {new Intl.NumberFormat(langueMobile(), { style: 'currency', currency: 'EUR' }).format(
                                commande.total_price,
                            )}
                        </Text>
                    )}
                </div>

                {commande.order_items.length > 0 && (
                    <ul className="flex flex-col gap-1.5">
                        {commande.order_items.map((item) => (
                            <li key={item.id} className="flex justify-between gap-3">
                                <Text variante="petit">{item.label ?? '—'}</Text>
                                {item.quantity != null && item.quantity > 1 && (
                                    <Text variante="petit" ton="attenue">
                                        ×{item.quantity}
                                    </Text>
                                )}
                            </li>
                        ))}
                    </ul>
                )}

                {/*
                 * ABSENTE sans client, jamais désactivée : ne montrer que ce qui
                 * aboutit. 88 commandes sur 1 684 sont dans ce cas.
                 */}
                {commande.customer_id && (
                    // mobile-check-ignore — ligne de navigation, libellé porté par
                    // le nom du client lui-même.
                    <button
                        type="button"
                        onClick={() => navigate(`/messaging/${commande.customer_id}`)}
                        className="flex min-h-touch items-center gap-2 border-t border-border-color-light pt-3 text-left"
                    >
                        <span className="min-w-0 flex-1">
                            <Text variante="petit" ton="marque" className="block truncate font-medium">
                                {commande.customer_name ?? t('commandes.feuille.voirConversation')}
                            </Text>
                            <Text variante="petit" ton="attenue" className="block">
                                {t('commandes.feuille.ouvrirConversation')}
                            </Text>
                        </span>
                        <ChevronRightIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-main-color" />
                    </button>
                )}

                {/* Le changement de statut reste au bureau, et on le DIT. */}
                <Text variante="petit" ton="attenue" className="block leading-snug">
                    {t('commandes.feuille.suiviSurOrdinateur')}
                </Text>
            </div>
        </BottomSheet>
    );
}
